import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Pagination, Skeleton,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';

import axiosInstance from '../../axiosInstance';
import { STATUTS } from './taskDialog';
import ChampDate from '../ChampDate/ChampDate';

const PAR_PAGE = 20;

const FILTRES_INITIAUX = { status: 'start', title: '', receiver: '',
                           review_at: '' };

const statut = (code) => STATUTS.find((s) => s.code === code);

const dateCourte = (v) => (v ? new Date(v).toLocaleDateString('fr-BE') : null);

/** Toutes les tâches de l'organisation, filtrables — pas celles d'un dossier. */
function TaskList({ onUpdateStats }) {
  const naviguer = useNavigate();

  const [taches, setTaches] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filtres, setFiltres] = useState(FILTRES_INITIAUX);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const { data } = await axiosInstance.get('/tasks/', {
        params: {
          status: filtres.status || undefined,
          title: filtres.title || undefined,
          receiver: filtres.receiver || undefined,
          review_at: filtres.review_at || undefined,
          page,
          page_size: PAR_PAGE,
        },
      });
      setTaches(data.results || []);
      setTotal(data.count || 0);
      onUpdateStats?.({ allTasks: data.count || 0 });
      setErreur('');
    } catch {
      setErreur("Les tâches n'ont pas pu être chargées.");
    } finally {
      setChargement(false);
    }
  }, [filtres, page, onUpdateStats]);

  // Les champs texte sont filtrés côté serveur : attendre le repos de la
  // frappe évite une requête par lettre.
  useEffect(() => {
    const minuterie = setTimeout(charger, 300);
    return () => clearTimeout(minuterie);
  }, [charger]);

  const filtrer = (champ) => (e) => {
    setFiltres((etat) => ({ ...etat, [champ]: e.target.value }));
    setPage(1);
  };

  const filtresActifs = JSON.stringify(filtres) !== JSON.stringify(FILTRES_INITIAUX);
  const pages = Math.ceil(total / PAR_PAGE);

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
             alignItems={{ md: 'flex-end' }} flexWrap="wrap" useFlexGap>
        <TextField select label="Statut" value={filtres.status}
                   onChange={filtrer('status')} sx={{ minWidth: 180 }}>
          <MenuItem value=""><em>Tous</em></MenuItem>
          {STATUTS.map(({ code, libelle }) => (
            <MenuItem key={code} value={code}>{libelle}</MenuItem>
          ))}
        </TextField>
        <TextField label="Titre" value={filtres.title}
                   onChange={filtrer('title')} sx={{ minWidth: 200 }} />
        <TextField label="Titulaire" value={filtres.receiver}
                   onChange={filtrer('receiver')} sx={{ minWidth: 200 }} />
        <ChampDate label="Échéance" value={filtres.review_at}
                   onChange={filtrer('review_at')}
                   InputLabelProps={{ shrink: true }}
                   sx={{ minWidth: 180 }} />
        {filtresActifs && (
          <Button size="small" onClick={() => { setFiltres(FILTRES_INITIAUX);
                                                setPage(1); }}>
            Réinitialiser
          </Button>
        )}
      </Stack>

      {erreur && <Alert severity="error">{erreur}</Alert>}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Statut</TableCell>
              <TableCell>Titre</TableCell>
              <TableCell>Titulaire</TableCell>
              <TableCell>Échéance</TableCell>
              <TableCell align="right">&nbsp;</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {chargement && taches.length === 0 && [...Array(5)].map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={5}><Skeleton height={28} /></TableCell>
              </TableRow>
            ))}

            {!chargement && taches.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} sx={{ py: 5, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary"
                              sx={{ mb: filtresActifs ? 2 : 0 }}>
                    Aucune tâche ne correspond.
                  </Typography>
                  {filtresActifs && (
                    <Button size="small"
                            onClick={() => { setFiltres(FILTRES_INITIAUX);
                                             setPage(1); }}>
                      Réinitialiser les filtres
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )}

            {taches.map((tache) => (
              <TableRow
                key={tache.id}
                hover
                sx={{ cursor: tache.expertise ? 'pointer' : 'default' }}
                onClick={() => tache.expertise
                  && naviguer(`/edit-expertise/${tache.expertise}`)}
              >
                <TableCell>
                  <Chip size="small"
                        color={statut(tache.status)?.couleur || 'default'}
                        label={statut(tache.status)?.libelle || tache.status} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {tache.title}
                  </Typography>
                </TableCell>
                <TableCell>
                  {tache.receiver || <Box component="span"
                                          sx={{ color: 'text.disabled' }}>—</Box>}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {dateCourte(tache.review_at)
                    || <Box component="span"
                            sx={{ color: 'text.disabled' }}>—</Box>}
                </TableCell>
                <TableCell align="right">
                  {tache.expertise && (
                    <Button size="small" variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              naviguer(`/edit-expertise/${tache.expertise}`);
                            }}>
                      Ouvrir
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {pages > 1 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
               alignItems="center" justifyContent="space-between">
          <Typography variant="body2" color="text.secondary">
            {(page - 1) * PAR_PAGE + 1}–{Math.min(page * PAR_PAGE, total)} sur {total}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {chargement && <CircularProgress size={16} />}
            <Pagination count={pages} page={page}
                        onChange={(_, v) => setPage(v)} />
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

export default TaskList;
